import PropTypes from "prop-types"
import { Link } from "react-router-dom"

import Image from "components/landing/image"
import Button from "components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import { cn } from "styles/utils"

// Keep these styles in sync with the loading shimmer `areas/landing/loaders/collection-menu.jsx`

const defaultBubbleStyle = {
  borderRadius: "50%",
  style: { height: "auto" },
}

const LandingCollectionMenu = ({
  element,
  handleItemClick,
  homepageQuickNavExp,
  lazyload,
}) => {
  const properties = element?.data?.properties || {}
  const {
    className: carouselClassName,
    opts,
    ...carouselProps
  } = properties?.carouselProps || {}
  const {
    className: carouselContentClassName,
    viewportClassName,
    ...carouselContentProps
  } = properties?.carouselContentProps || {}
  const { className: carouselItemClassName, ...carouselItemProps } =
    properties?.carouselItemProps || {}
  const { className: itemTitleClassName, ...itemTitleProps } =
    properties?.itemTitleProps || {}
  const { className: carouselControlsClassName } =
    properties?.carouselControlsProps || {}
  const { disableSkip } = properties || {}

  return (
    <div {...properties?.containerProps}>
      <Carousel
        className={cn(
          element?.contain && homepageQuickNavExp !== 2 && "contained-wide",
          carouselClassName
        )}
        description={element?.data?.description}
        disableSkip={disableSkip}
        opts={{
          active: true,
          align: "start",
          loop: false,
          slidesToScroll: "auto",
          ...(homepageQuickNavExp === 2
            ? {
                breakpoints: {
                  "(min-width: 1440px)": { active: false },
                },
                dragFree: true,
              }
            : {
                breakpoints: {
                  "(min-width: 1280px)": { active: false },
                },
              }),
          ...opts,
        }}
        {...carouselProps}
      >
        <CarouselContent
          className={cn(
            homepageQuickNavExp === 2
              ? "widescreen:justify-center xlarge:gap-16 gap-8 py-12"
              : "large:justify-center tablet:gap-16 widescreen:gap-24 xlarge:gap-40 justify-start gap-8",
            carouselContentClassName
          )}
          {...(homepageQuickNavExp !== 2 && {
            style: {
              "--base-basis": "25vw",
              "--desktop-basis": "13vw",
              "--large-basis": "140px",
              "--tablet-basis": "18vw",
              "--xlarge-basis": "180px",
              ...(homepageQuickNavExp === 1 && {
                "--base-basis": "20vw",
                "--desktop-basis": "10vw",
                "--large-basis": "120px",
                "--tablet-basis": "15vw",
                "--xlarge-basis": "150px",
              }),
            },
          })}
          viewportClassName={cn(
            "px-16",
            homepageQuickNavExp === 2
              ? null
              : homepageQuickNavExp === 1
                ? "py-12"
                : cn(
                    "mask-fade-right large:mask-none widescreen:py-60 tablet:px-32 widescreen:px-60 px-16 py-32",
                    viewportClassName
                  )
          )}
          {...carouselContentProps}
        >
          {element.items.map((item, i) => {
            const image = {
              ...item.image,
              alt: "",
              sizes:
                homepageQuickNavExp === 2
                  ? "(min-width: 1280px) 32px, 24px"
                  : homepageQuickNavExp === 1
                    ? "(min-width: 1920px) 150px, (min-width: 1280px) 120px, (min-width: 1024px) 10vw, (min-width: 768px) 15vw, 20vw"
                    : "(min-width: 1920px) 180px, (min-width: 1280px) 140px, (min-width: 1024px) 13vw, (min-width: 768px) 18vw, 25vw",
              style: {
                ...(homepageQuickNavExp === 2 && {
                  borderRadius: "50%",
                  height: "auto",
                  width: "100%",
                }),
                ...defaultBubbleStyle,
                ...item.image?.style,
              },
            }
            const { className, ...titleProps } = item.title_style || {}
            return (
              <CarouselItem
                className={cn(
                  homepageQuickNavExp === 2
                    ? "basis-auto"
                    : "xlarge:basis-(--xlarge-basis) large:basis-(--large-basis) desktop:basis-(--desktop-basis) basis-(--base-basis) tablet:basis-(--tablet-basis) text-center",
                  carouselItemClassName
                )}
                key={`${element.id}-${item.id}-${i}`}
                {...carouselItemProps}
              >
                <Button
                  asChild
                  className={cn(
                    homepageQuickNavExp === 2
                      ? "border-grey-300 h-auto gap-8 whitespace-nowrap border px-12 py-8"
                      : "block h-full"
                  )}
                  variant={homepageQuickNavExp === 2 ? "tertiary" : null}
                >
                  <Link
                    data-lc={element.anchor_name || element.id}
                    onClick={() => handleItemClick && handleItemClick(item.url)}
                    to={item.url}
                  >
                    <Image
                      containerStyle={
                        homepageQuickNavExp === 2
                          ? {
                              height: { _: 24, large: 32 },
                              width: { _: 24, large: 32 },
                            }
                          : { px: 1 }
                      }
                      image={image}
                      lazyload={lazyload}
                    />
                    <h2
                      className={cn(
                        homepageQuickNavExp === 2
                          ? "text-14 font-medium text-black"
                          : homepageQuickNavExp === 1
                            ? "text-12 mt-4 px-4"
                            : "body widescreen:mt-8 tablet:px-8 xlarge:px-16 mt-8 px-12 font-semibold",
                        itemTitleClassName,
                        className
                      )}
                      {...itemTitleProps}
                      {...titleProps}
                    >
                      {item.title}
                    </h2>
                  </Link>
                </Button>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselPrevious
          className={cn(
            homepageQuickNavExp === 2
              ? "widescreen:hidden inline-flex"
              : "large:hidden inline-flex",
            carouselControlsClassName
          )}
        />
        <CarouselNext
          className={cn(
            homepageQuickNavExp === 2
              ? "widescreen:hidden inline-flex"
              : "large:hidden inline-flex",
            carouselControlsClassName
          )}
        />
      </Carousel>
    </div>
  )
}

LandingCollectionMenu.propTypes = {
  element: PropTypes.object,
  handleItemClick: PropTypes.func,
  homepageQuickNavExp: PropTypes.number,
  lazyload: PropTypes.bool,
}

export default LandingCollectionMenu
